import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { ApiWaveDecision } from "@/generated/models/ApiWaveDecision";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { prepareWaveDecisionPoint } from "@/helpers/waves/wave-decision.helpers";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import { fetchWaveDecisionsV2 } from "@/services/api/wave-decisions-v2-api";
interface UseWaveDecisionsProps {
  readonly waveId: string;
  readonly wave?: ApiWave | ApiWaveMin | undefined;
  readonly enabled?: boolean | undefined;
  readonly loadAllPages?: boolean | undefined;
  readonly pageSize?: number | undefined;
}
const DEFAULT_PAGE = 1;
const DEFAULT_WAVE_DECISIONS_PAGE_SIZE = 100;
export const FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE = 2000;
const DEFAULT_STALE_TIME = 60000;
const getValidPageSize = (pageSize: number | undefined): number => {
  if (typeof pageSize === "number" && Number.isFinite(pageSize) && pageSize > 0) {
    return Math.min(Math.floor(pageSize), FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE);
  }
  return DEFAULT_WAVE_DECISIONS_PAGE_SIZE;
};
export function useWaveDecisions(t0) {
  const $ = _c(37);
  const {
    waveId,
    wave,
    enabled: t1,
    loadAllPages: t2,
    pageSize
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const loadAllPages = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== pageSize) {
    t3 = getValidPageSize(pageSize);
    $[0] = pageSize;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const resolvedPageSize = t3;
  let t4;
  if ($[2] !== resolvedPageSize || $[3] !== waveId) {
    t4 = [QueryKey.WAVE_DECISIONS, {
      waveId,
      pageSize: resolvedPageSize
    }];
    $[2] = resolvedPageSize;
    $[3] = waveId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== resolvedPageSize || $[6] !== wave || $[7] !== waveId) {
    t5 = async t6 => {
      const {
        pageParam,
        signal
      } = t6;
      const currentPage = pageParam ?? DEFAULT_PAGE;
      return await fetchWaveDecisionsV2({
        waveId,
        wave,
        params: {
          sort_direction: "DESC",
          sort: "decision_time",
          page: currentPage.toString(),
          page_size: resolvedPageSize.toString()
        },
        signal
      });
    };
    $[5] = resolvedPageSize;
    $[6] = wave;
    $[7] = waveId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== enabled || $[10] !== t4 || $[11] !== t5) {
    t6 = {
      queryKey: t4,
      queryFn: t5,
      initialPageParam: DEFAULT_PAGE,
      getNextPageParam: _temp,
      enabled,
      staleTime: DEFAULT_STALE_TIME
    };
    $[9] = enabled;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    data,
    isError,
    error,
    refetch,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchNextPageError,
    isFetchingNextPage
  } = useInfiniteQuery(t6);
  let t7;
  let t8;
  if ($[13] !== enabled || $[14] !== fetchNextPage || $[15] !== hasNextPage || $[16] !== isFetchNextPageError || $[17] !== isFetchingNextPage || $[18] !== loadAllPages) {
    t7 = () => {
      if (!enabled || !loadAllPages || !hasNextPage || isFetchNextPageError || isFetchingNextPage) {
        return;
      }
      fetchNextPage();
    };
    t8 = [enabled, fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage, loadAllPages];
    $[13] = enabled;
    $[14] = fetchNextPage;
    $[15] = hasNextPage;
    $[16] = isFetchNextPageError;
    $[17] = isFetchingNextPage;
    $[18] = loadAllPages;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  useEffect(t7, t8);
  data?.pages;
  let t9;
  if ($[21] !== data?.pages || $[22] !== waveId) {
    const mergedDecisionPoints = [];
    const seenDecisionTimes = new Set();
    for (const page of data?.pages ?? []) {
      for (const decisionPoint of page.data) {
        if (seenDecisionTimes.has(decisionPoint.decision_time)) {
          continue;
        }
        seenDecisionTimes.add(decisionPoint.decision_time);
        mergedDecisionPoints.push(prepareWaveDecisionPoint(decisionPoint, waveId));
      }
    }
    t9 = mergedDecisionPoints.sort(_temp2);
    $[21] = data?.pages;
    $[22] = waveId;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const decisionPoints = t9;
  const hasLoadedAllPages = Boolean(data?.pages.length) && hasNextPage === false && !isFetchingNextPage;
  const isLoadingAllPages = Boolean(enabled && (isLoading || loadAllPages && (isFetchingNextPage || !isFetchNextPageError && Boolean(hasNextPage))));
  const isLoadingAllPagesError = Boolean(enabled && loadAllPages && !hasLoadedAllPages && !isLoading && !isFetching && !isFetchingNextPage && (isError || isFetchNextPageError));
  const t10 = Boolean(hasNextPage);
  let t11;
  if ($[24] !== decisionPoints || $[25] !== error || $[26] !== fetchNextPage || $[27] !== hasLoadedAllPages || $[28] !== isError || $[29] !== isFetching || $[30] !== isFetchingNextPage || $[31] !== isLoading || $[32] !== isLoadingAllPages || $[33] !== isLoadingAllPagesError || $[34] !== refetch || $[35] !== t10) {
    t11 = {
      decisionPoints,
      isError,
      error,
      refetch,
      isFetching,
      isLoading,
      hasLoadedAllPages,
      isLoadingAllPages,
      isLoadingAllPagesError,
      fetchNextPage,
      hasNextPage: t10,
      isFetchingNextPage
    };
    $[24] = decisionPoints;
    $[25] = error;
    $[26] = fetchNextPage;
    $[27] = hasLoadedAllPages;
    $[28] = isError;
    $[29] = isFetching;
    $[30] = isFetchingNextPage;
    $[31] = isLoading;
    $[32] = isLoadingAllPages;
    $[33] = isLoadingAllPagesError;
    $[34] = refetch;
    $[35] = t10;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}
function _temp2(a, b) {
  return a.decision_time - b.decision_time;
}
function _temp(lastPage) {
  return lastPage.next ? lastPage.page + 1 : undefined;
}